import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import { CellContext } from "@/lib/Components/DataTable/types";
import {
  ContactListItem,
  contactListQuery,
} from "@/app/Contacts/GraphQL/contactListQuery";
import { ContactRecord } from "@/app/Contacts/GraphQL/contactRecordQuery";
import { ContactListQueryVariables } from "@/gql/graphql";
import { ReactNode } from "react";
import { useContactActions } from "@/app/Contacts/Hooks/useContactActions";
import { ArrowLeftStartOnRectangleIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/catalyst/button";
import { useNavigate } from "@tanstack/react-router";
import { formatOrdinal } from "@/lib/Formatters/formatOrdinal";

type ContactTableColumnId =
  | "name"
  | "company_number"
  | "address"
  | "po_number"
  | "invoice_date";

export function ContactEntityTable({
  id,
  queryVariables,
  rightButtons,
  title,
}: {
  id: TableId;
  queryVariables?: Partial<ContactListQueryVariables>;
  rightButtons?: ReactNode;
  title: string;
}) {
  const getActions = useContactActions();
  const navigate = useNavigate();

  const columns: DataTableColDef<
    ContactListItem,
    unknown,
    ContactTableColumnId
  >[] = [
    {
      id: "name",
      header: "Name",
      accessorFn: (row) => row,
      cell: ({ row }) => {
        return (
          <div>
            <Button
              plain
              className="text-blue-500"
              onClick={() => {
                navigate({
                  to: ".",
                  search: {
                    contactId: row.original.id,
                  },
                });
              }}
            >
              {row.original.name} <ArrowLeftStartOnRectangleIcon />
            </Button>
          </div>
        );
      },
    },
    {
      id: "company_number",
      header: "Business number",
      accessorFn: (row) => row.company_number ?? "--",
    },
    {
      id: "po_number",
      header: "Purchase Order #",
      accessorFn: (row) => row.purchase_order_number ?? "--",
    },
    {
      id: "invoice_date",
      header: "Invoice date",
      accessorFn: (row) => formatOrdinal(row.billing_cycle_day_of_month),
    },
    {
      id: "address",
      header: "Address",
      accessorFn: (row) => row,
      cell: AddressCell,
    },
  ];

  return (
    <DataTable
      title={title}
      columns={columns}
      document={contactListQuery}
      rightButtons={rightButtons}
      getActions={getActions}
      getQueryVariables={({ pagination, search }) => {
        return {
          search: search,
          first: pagination.pageSize,
          page: pagination.pageIndex,
          ...queryVariables,
        };
      }}
      accessor={(data) => data.contacts}
      id={id}
    />
  );
}

function AddressCell({ row }: CellContext<ContactRecord, any>) {
  const model = row.original;

  return (
    <div>
      <p>{model.billingAddress?.formatted_address_line_1}</p>
      <p>{model.billingAddress?.formatted_address_line_2}</p>
    </div>
  );
}
